import { addQueryParamsUrl } from '@repo/common-utils';
import { useCommonRouter } from '@repo/platform';
import { PageViewEventLogger, useUserEventLogger } from '@repo/tracking-service';
import { BoxButton, Description, LottiePlayer, Spinner, useDialog } from '@repo/ui';
import { useAppSignInContext, useAuthContext } from '@repo/user-session';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useRef, useState } from 'react';

import { AFTER_CI_QUERY_STATE, CI_AUTHENTICATION_ERROR, CI_REQUEST_TYPES } from '../constants';
import { ciReturnUrlAtom, isPreventRouteChangeAtom, termsApprovalStateAtom } from '../store';
import { CiAuthenticationErrors, CiRequestTypes } from '../types';

export const CIAuthentication = () => {
  const router = useCommonRouter();
  const { authService, setStatusSignOut } = useAuthContext();
  const { resetSignRequiredStep } = useAppSignInContext();
  const [termsApprovalState, setTermsApprovalState] = useAtom(termsApprovalStateAtom);
  const [isPreventRouterChange, setIsPreventRouteChange] = useAtom(isPreventRouteChangeAtom);
  const [ciReturnUrl, setCiReturnUrl] = useAtom(ciReturnUrlAtom);
  const [queryState, setQueryState] = useState('');
  const [requestType, setRequestType] = useState<CiRequestTypes | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [verificationCode, setVerificationCode] = useState<string | null>(null);
  const { showDialog } = useDialog();
  const { sendViewEvent, sendClickEvent } = useUserEventLogger();
  const isMount = useRef(false);

  const requestUpdateTermsAgree = useCallback(() => {
    if (!termsApprovalState) return;
    const termKeys = Object.keys(termsApprovalState);
    if (termKeys.length === 0) return;
    authService?.updateTermsAgree(
      termKeys.map((termKey) => {
        return { termKey: termKey, isApproved: termsApprovalState[termKey] ?? true };
      })
    );
  }, [authService, termsApprovalState]);

  const successCiAuthentication = () => {
    resetSignRequiredStep();
    if (termsApprovalState) {
      requestUpdateTermsAgree();
      setTermsApprovalState(undefined);
    }
    if (requestType === CI_REQUEST_TYPES.COLLECTION || requestType === CI_REQUEST_TYPES.USER_UPDATE) {
      authService?.getUser();
    }

    sendClickEvent('auth-phone-confirm_next', { requestType, returnUrl: ciReturnUrl, state: queryState });
    const successQueryParams: { state: string; request_type: CiRequestTypes; ci_result: string; verification_code?: string | null } = {
      state: AFTER_CI_QUERY_STATE,
      request_type: requestType ?? CI_REQUEST_TYPES.COLLECTION,
      ci_result: 'success',
      verification_code: verificationCode
    };
    if (!verificationCode) {
      delete successQueryParams.verification_code;
    }
    router.push(
      ciReturnUrl ? addQueryParamsUrl(ciReturnUrl, successQueryParams as Record<string, string>) : (authService?.signService.signInSuccessUrl ?? '/')
    );
  };

  const failureCiAuthentication = useCallback(async () => {
    if (!authService) return;
    console.error('CI Authentication failed with error code:', errorCode);
    const errorDialogData = CI_AUTHENTICATION_ERROR[errorCode as CiAuthenticationErrors] ?? CI_AUTHENTICATION_ERROR.ci_process_failed;
    let returnUrl = authService.signPaths.defaultReturn;
    if (errorCode === 'under_age_signup') {
      returnUrl = '/';
      errorDialogData.buttons = {
        primary: {
          text: '돌아가기',
          onClick: () => {
            sendClickEvent('sign-up-error_modal_back', { errorCode });
            setStatusSignOut(true);
          }
        }
      };
      sendViewEvent('sign-up-error_modal', undefined, { isAllowRetry: true });
    } else if (requestType === CI_REQUEST_TYPES.USER_UPDATE) {
      returnUrl = addQueryParamsUrl(ciReturnUrl ?? authService.signPaths.ciCollectionRequest, {
        state: AFTER_CI_QUERY_STATE,
        ci_result: 'failure'
      });
    } else if (requestType === CI_REQUEST_TYPES.COLLECTION) {
      returnUrl = `${authService.signPaths.ciCollectionRequest}${ciReturnUrl ? `?return_url=${encodeURIComponent(addQueryParamsUrl(ciReturnUrl, { state: AFTER_CI_QUERY_STATE }))}` : ''}`;
    }

    await showDialog(errorDialogData);
    router.push(returnUrl);
    setCiReturnUrl(null);
  }, [authService, ciReturnUrl, errorCode, requestType, router, sendClickEvent, sendViewEvent, setCiReturnUrl, setStatusSignOut, showDialog]);

  useEffect(() => {
    if (isMount.current || !queryState) return;
    if (isPreventRouterChange) {
      setTimeout(() => {
        setIsPreventRouteChange(false);
      }, 1000);
      return;
    }

    if (queryState === 'failure') {
      failureCiAuthentication();
    }
    isMount.current = true;
  }, [failureCiAuthentication, isPreventRouterChange, queryState, setIsPreventRouteChange]);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    setQueryState(searchParams.get('state') ?? '');
    setRequestType((searchParams.get('request_type') as CiRequestTypes) ?? CI_REQUEST_TYPES.COLLECTION);
    setVerificationCode(searchParams.get('verification_code'));
    setErrorCode(searchParams.get('error_code'));
  }, []);

  if (queryState !== 'success') {
    return <Spinner />;
  }
  return (
    <>
      <Description
        graphic={<LottiePlayer src={'/lotties/ani_check.json'} loop={false} className={'w-20 h-20'} />}
        title={'인증이 안전하게 완료됐어요'}
        description={'인증이 정상적으로 확인됐어요.\n계속해서 다음으로 이동해주세요.'}
        button={
          <BoxButton size={'medium'} onClick={successCiAuthentication}>
            다음
          </BoxButton>
        }
      />
      {requestType && <PageViewEventLogger pageName={'auth-phone-confirm'} eventProperty={{ requestType, returnUrl: ciReturnUrl, state: queryState }} />}
    </>
  );
};
